import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { ChevronDown, User, LogOut, Sparkles } from "lucide-react";
import "./Header.css";

function Header() {
  const { showAuthModal, user, logout } = useAuth();
  const [showDropdown, setShowDropdown] = useState(false);
  const [imageLoadError, setImageLoadError] = useState(false);
  const [dropdownImageLoadError, setDropdownImageLoadError] = useState(false);
  const [logoLoadError, setLogoLoadError] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const logoSrc = "/logo-s.png";

  const getUserInitials = (email) => {
    if (!email) return "U";
    const name = email.split("@")[0];
    return name.charAt(0).toUpperCase();
  };

  const handleSignOut = () => {
    logout();
    setShowDropdown(false);
  };

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  const getProfileImageUrl = () => {
    return user?.photoURL || null;
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setImageLoadError(false);
    setDropdownImageLoadError(false);
  }, [user?.photoURL]);

  if (location.pathname === "/generate") return null;

  const profileImageUrl = getProfileImageUrl();
  const shouldShowImage = profileImageUrl && profileImageUrl.trim() !== "" && !imageLoadError;
  const shouldShowDropdownImage = profileImageUrl && profileImageUrl.trim() !== "" && !dropdownImageLoadError;

  return (
    <header className="header header-default light">
      {/* Logo Section */}
      <Link to="/" className="logo-container">
        {!logoLoadError ? (
          <>
            <img 
              src={logoSrc}
              alt="Slideprintz Logo" 
              className="header-logo"
              onError={() => setLogoLoadError(true)}
              onLoad={() => setLogoLoadError(false)}
            />
            <h1 className="logo-text">Slideprintz</h1>
          </>
        ) : (
          <h1 className="title">Slideprintz</h1>
        )}
      </Link>

      {/* Right Side - Auth/User Section */}
      <div className="button-group">
        {user ? (
          <div className="user-section" ref={dropdownRef}>
            <div className="user-info">
              <div className="profile-dropdown">
                <button 
                  className="profile-button" 
                  onClick={toggleDropdown}
                  aria-expanded={showDropdown}
                  aria-haspopup="true"
                  aria-label="User menu"
                >
                  <div className="profile-avatar">
                    {shouldShowImage ? (
                      <img 
                        src={profileImageUrl}
                        alt="Profile" 
                        className="profile-image"
                        onError={() => setImageLoadError(true)}
                        onLoad={() => setImageLoadError(false)}
                      />
                    ) : (
                      <span className="profile-initials">
                        {getUserInitials(user.email)}
                      </span>
                    )}
                  </div>
                  <ChevronDown 
                    className={`dropdown-arrow ${showDropdown ? "open" : ""}`}
                    size={12}
                  />
                </button>
                
                {showDropdown && (
                  <div className="dropdown-menu">
                    <div className="dropdown-header">
                      <div className="dropdown-avatar">
                        {shouldShowDropdownImage ? (
                          <img 
                            src={profileImageUrl} 
                            alt="Profile" 
                            className="dropdown-profile-image"
                            onError={() => setDropdownImageLoadError(true)}
                            onLoad={() => setDropdownImageLoadError(false)}
                          />
                        ) : (
                          <span className="dropdown-profile-initials">
                            {getUserInitials(user.email)}
                          </span>
                        )}
                      </div>
                      <div className="dropdown-user-info">
                        <div className="dropdown-email">{user.email}</div>
                      </div>
                    </div>
                    
                    <div className="dropdown-divider"></div>
                    
                    <div className="dropdown-items">
                      <Link 
                        to="/generate" 
                        className="dropdown-item-header" 
                        onClick={() => setShowDropdown(false)}
                      >
                        <Sparkles className="dropdown-icon-header" size={16} />
                        Create Project
                      </Link>
                      
                      <Link 
                        to="/profile" 
                        className="dropdown-item-header"
                        onClick={() => setShowDropdown(false)}
                      >
                        <User className="dropdown-icon-header" size={16} />
                        Profile
                      </Link>
                    </div>
                    
                    <div className="dropdown-divider"></div>
                    
                    <button 
                      className="dropdown-item-header logout-item"
                      onClick={handleSignOut}
                    >
                      <LogOut className="dropdown-icon-header" size={16} />
                      Log Out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <>
            <button
              className="btn-outline-header"
              onClick={() => showAuthModal("login")}
            >
              Log In
            </button>
            <button
              className="btn-primary-header"
              onClick={() => showAuthModal("signup")}
            >
              Sign Up for Free
            </button>
          </>
        )}
      </div>
    </header>
  );
}

export default Header;